import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  FileText, Plus, Send, Download, Eye, Edit3, Trash2, Loader2,
  Sparkles, Users, Upload, X, Radio, ShieldCheck, Building2, Zap,
  Landmark,
} from "lucide-react";
import { suggestHsn, lookupHsn } from "../lib/constructionHsn";

const inr = (n) => `₹${Number(Math.round(n) || 0).toLocaleString("en-IN")}`;

const EMPTY_ITEM = { name: "", hsn: "", qty: 1, unit: "MT", rate: 0, brand: "" };
const EMPTY_RECIPIENT = { name: "", email: "", gstin: "", pan: "", address: "" };

const PAYMENT_TERM_PRESETS = [
  "50% advance · 50% against dispatch",
  "100% advance",
  "30% advance · 70% against delivery",
  "Net 30 days from invoice",
  "Letter of Credit at sight",
  "Cash Against Documents",
];

/**
 * AdminMonumentalStudio
 * ─────────────────────
 * Elite admin-only Studio for drafting, editing, previewing, issuing,
 * and transmitting the "Monumental" Purchase Order and Quotation PDFs.
 * Supports LISTED (Vinci-registered) and UNLISTED (free-form email +
 * GSTIN) recipients with optional CSV bulk-transmit.
 */
export function AdminMonumentalStudio({ api }) {
  const [kind, setKind] = useState("po");          // po | quote | mandate
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draftOpen, setDraftOpen] = useState(false);
  const [editing, setEditing] = useState(null);    // full doc row when editing
  const [transmit, setTransmit] = useState(null);  // row for transmit dialog

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/monumental?kind=${kind}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load documents");
    } finally { setLoading(false); }
  }, [api, kind]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const kindLabel = kind === "po" ? "Purchase Orders"
                   : kind === "quote" ? "Quotations"
                   : "Welcome On-Board Mandates";
  const newLabel = kind === "po" ? "New PO"
                 : kind === "quote" ? "New Quote"
                 : "New Mandate";
  const blurb = kind === "mandate"
    ? "Manually generate the Sovereign Executive Vellum for any partner — the Vinci Common Seal, 3D Vinci Board seal, and a personalised induction letter, ready to transmit."
    : `Draft, edit and transmit gold-on-black authoritative ${kind === "po" ? "purchase orders" : "quotations"} — HSN-strict material grids, glowing VNC-SRN case files, 3D Vinci Trade Desk seal. Send to listed partners or free-form emails · CSV bulk supported.`;

  return (
    <div data-testid="admin-monumental-studio" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#D8B23E]">Monumental Studio</div>
          <h2 className="font-serif text-3xl mt-2">Elite {kindLabel}</h2>
          <p className="text-[13px] text-[#8A8A82] font-body mt-1 max-w-2xl">
            {blurb}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="monumental-kind-po"
                    onClick={() => setKind("po")}
                    className={`font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-full transition-colors
                                ${kind === "po" ? "bg-[#D8B23E] text-black"
                                                   : "border border-white/20 text-[#C9C9C2] hover:border-[#D8B23E]"}`}>
            <FileText size={12} className="inline mr-1.5 -mt-0.5" /> Purchase Orders
          </button>
          <button data-testid="monumental-kind-quote"
                    onClick={() => setKind("quote")}
                    className={`font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-full transition-colors
                                ${kind === "quote" ? "bg-[#D8B23E] text-black"
                                                      : "border border-white/20 text-[#C9C9C2] hover:border-[#D8B23E]"}`}>
            <Radio size={12} className="inline mr-1.5 -mt-0.5" /> Quotations
          </button>
          <button data-testid="monumental-kind-mandate"
                    onClick={() => setKind("mandate")}
                    className={`font-grotesk text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-full transition-colors
                                ${kind === "mandate" ? "bg-[#D8B23E] text-black"
                                                        : "border border-white/20 text-[#C9C9C2] hover:border-[#D8B23E]"}`}>
            <Landmark size={12} className="inline mr-1.5 -mt-0.5" /> Welcome Mandates
          </button>
          <button data-testid="monumental-new-btn"
                    onClick={() => { setEditing(null); setDraftOpen(true); }}
                    className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk uppercase tracking-[0.22em] text-[10px] px-4 py-2 rounded-full">
            <Plus size={12} className="inline mr-1.5 -mt-0.5" /> {newLabel}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[#8A8A82] py-12 text-center"><Loader2 className="inline animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-sm p-12 text-center text-[#8A8A82] font-body">
          No {kindLabel.toLowerCase()} yet. Click <b>{newLabel}</b> above to draft your first one.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <DocRow key={r.id} row={r} api={api}
                      onEdit={() => { setEditing(r); setDraftOpen(true); }}
                      onTransmit={() => setTransmit(r)}
                      onChanged={load} />
          ))}
        </div>
      )}

      {draftOpen && kind === "mandate" && (
        <MandateEditorSheet
          api={api}
          initial={editing}
          onClose={() => { setDraftOpen(false); setEditing(null); }}
          onSaved={() => { setDraftOpen(false); setEditing(null); load(); }}
        />
      )}
      {draftOpen && kind !== "mandate" && (
        <DraftEditorSheet
          api={api}
          kind={kind}
          initial={editing}
          onClose={() => { setDraftOpen(false); setEditing(null); }}
          onSaved={() => { setDraftOpen(false); setEditing(null); load(); }}
        />
      )}
      {transmit && (
        <TransmitSheet
          api={api}
          row={transmit}
          onClose={() => setTransmit(null)}
          onDone={() => { setTransmit(null); load(); }}
        />
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Document Row
   ═════════════════════════════════════════════════════════════════ */
function DocRow({ row, api, onEdit, onTransmit, onChanged }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const isIssued = row.status === "issued";
  const isMandate = row.kind === "mandate";
  const rec = row.recipient || {};
  const totals = row.totals || {};
  const [busy, setBusy] = useState(false);

  const issue = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/monumental/${row.id}/issue`);
      toast.success(`Issued · ${row.doc_number}`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Issue failed");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete draft ${row.doc_number}?`)) return;
    try {
      await api.delete(`/admin/monumental/${row.id}`);
      toast.success("Draft deleted");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <motion.div
      data-testid={`monumental-row-${row.id}`}
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className="border border-white/10 hover:border-[#D8B23E]/40 rounded-sm p-4 bg-[#0A0906]"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-start">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-[13px] text-[#D8B23E]">{row.doc_number}</span>
            {!isMandate && <span className="font-mono text-[10px] text-[#8A8A82]">{row.srn}</span>}
            <span className={`font-grotesk text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full
                                ${isIssued ? "bg-[#2FB999]/20 text-[#2FB999] border border-[#2FB999]/50"
                                             : "bg-[#D8B23E]/15 text-[#D8B23E] border border-[#D8B23E]/40"}`}>
              {isIssued ? "Issued" : "Draft"}
            </span>
            {isMandate && (
              <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E] inline-flex items-center gap-1">
                <Landmark size={10} /> Welcome Mandate
              </span>
            )}
            {rec.listed && (
              <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#2FB999] inline-flex items-center gap-1">
                <ShieldCheck size={10} /> Listed
              </span>
            )}
          </div>
          <div className="mt-2 text-[#F5F5F0] font-body flex items-center gap-2 flex-wrap">
            <Building2 size={14} className="text-[#8A6A1A]" />
            <b>{rec.name || "—"}</b>
            <span className="text-[#8A8A82]">· {rec.email || "—"}</span>
            {isMandate && rec.business_name && (
              <span className="text-[#8A8A82] italic">· {rec.business_name}</span>
            )}
            {!isMandate && rec.gstin && (
              <span className="font-mono text-[11px] text-[#8A8A82]">GSTIN {rec.gstin}</span>
            )}
          </div>
          <div className="mt-1 text-[11px] text-[#8A8A82] font-body">
            {isMandate ? (
              <>
                Vinci Code{" "}
                <b className="font-mono text-[#F6E4A6]">{rec.vinci_code || "—"}</b>
                {row.created_at && <> · Drafted {new Date(row.created_at).toLocaleDateString()}</>}
              </>
            ) : (
              <>
                {(row.items || []).length} line item(s) · Grand Total{" "}
                <b style={{ color: "#F6E4A6" }}>{inr(totals.grand_total || 0)}</b>
                {row.delivery_date && <> · Delivery {new Date(row.delivery_date).toLocaleDateString()}</>}
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          <a href={`${backend}/api/admin/monumental/${row.id}/preview.pdf`}
               target="_blank" rel="noreferrer"
               data-testid={`monumental-preview-${row.id}`}
               className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-white/20 hover:border-[#D8B23E] text-[#C9C9C2] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
            <Eye size={11} /> Preview
          </a>
          {!isIssued && (
            <>
              <button data-testid={`monumental-edit-${row.id}`}
                        onClick={onEdit}
                        className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-white/20 hover:border-[#D8B23E] text-[#C9C9C2] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                <Edit3 size={11} /> Edit
              </button>
              <button data-testid={`monumental-issue-${row.id}`}
                        onClick={issue} disabled={busy}
                        className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                Generate PDF
              </button>
              <button data-testid={`monumental-delete-${row.id}`}
                        onClick={del}
                        className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B]/10 px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                <Trash2 size={11} /> Delete
              </button>
            </>
          )}
          {isIssued && (
            <>
              <a href={`${backend}/api/admin/monumental/${row.id}/pdf`}
                   target="_blank" rel="noreferrer"
                   data-testid={`monumental-download-${row.id}`}
                   className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-white/20 hover:border-[#D8B23E] text-[#C9C9C2] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                <Download size={11} /> Download
              </a>
              <button data-testid={`monumental-transmit-${row.id}`}
                        onClick={onTransmit}
                        className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#2FB999] to-[#5FD3B7] px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                <Send size={11} /> Transmit
              </button>
            </>
          )}
        </div>
      </div>

      {row.last_transmit_at && (
        <div className="mt-2 text-[10px] font-body text-[#8A8A82] italic">
          Last transmitted {new Date(row.last_transmit_at).toLocaleString()}
          · {(row.transmissions || []).reduce((s, t) => s + (t.delivered || 0), 0)} delivered total
        </div>
      )}
    </motion.div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Mandate Editor Sheet · Welcome On-Board Mandate
   ═════════════════════════════════════════════════════════════════ */
function MandateEditorSheet({ api, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.recipient?.name || "");
  const [email, setEmail] = useState(initial?.recipient?.email || "");
  const [businessName, setBusinessName] = useState(initial?.recipient?.business_name || "");
  const [vinciCode, setVinciCode] = useState(initial?.recipient?.vinci_code || "");
  const [note, setNote] = useState(initial?.note || "");
  const [busy, setBusy] = useState(false);

  const validate = () => {
    if (!name.trim()) return "Recipient name required";
    if (!email.includes("@")) return "Valid recipient email required";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    const recipient = {
      name, email,
      business_name: businessName,
      vinci_code: vinciCode,
    };
    try {
      if (isEdit) {
        await api.patch(`/admin/monumental/mandate/${initial.id}`,
                        { recipient, note });
        toast.success("Mandate updated");
      } else {
        await api.post("/admin/monumental/mandate/draft",
                       { recipient, note });
        toast.success("Mandate drafted");
      }
      onSaved?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : (d || "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="mandate-draft-sheet"
         className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-[#0A0906] border border-[#D8B23E]/40 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto"
           style={{ boxShadow: "0 40px 120px -30px rgba(216,178,62,0.4)" }}>
        <div className="sticky top-0 bg-[#0A0906] border-b border-[#D8B23E]/30 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="overline text-[#D8B23E] inline-flex items-center gap-2">
              <Landmark size={12} /> Sovereign Vellum
            </div>
            <h3 className="font-serif text-2xl">{isEdit ? "Edit" : "New"} Welcome On-Board Mandate</h3>
            <p className="text-[11px] text-[#8A8A82] font-body mt-1 max-w-md">
              Renders the Executive Vellum PDF with the Vinci Common Seal, 3D Vinci Board seal and the "Veni · Vidi · Vici" mantra — ready to preview, issue and email.
            </p>
          </div>
          <button onClick={onClose} data-testid="mandate-draft-close"
                    className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          <section>
            <div className="overline text-[#B87333]">Recipient</div>
            <h4 className="font-serif text-lg mt-1">Presented To</h4>
            <p className="text-[11px] text-[#8A8A82] font-body mt-1">
              Existing Vinci partners get auto-enriched (business name + Vinci code) when you type their email.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <TextField label="Full Name" required
                          value={name} onChange={setName}
                          testid="mandate-recipient-name" />
              <TextField label="Email" required type="email"
                          value={email} onChange={setEmail}
                          testid="mandate-recipient-email" />
              <TextField label="Business / Company"
                          value={businessName} onChange={setBusinessName}
                          testid="mandate-recipient-business" />
              <TextField label="Vinci Code (optional)"
                          value={vinciCode}
                          onChange={(v) => setVinciCode(v.toUpperCase())}
                          placeholder="VN-5151-0042"
                          testid="mandate-recipient-code" />
            </div>
          </section>

          <section>
            <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A] font-grotesk">
              Personal Induction Note (optional)
            </label>
            <textarea data-testid="mandate-note"
                        value={note} onChange={(e) => setNote(e.target.value)}
                        rows={3}
                        placeholder="A personal line the Chairman wants included in the email body — e.g. 'Looking forward to the first cement dispatch this week.'"
                        className="w-full mt-1 bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] text-sm" />
          </section>

          <div className="border-t border-white/10 pt-4 flex items-center justify-between flex-wrap gap-3">
            <div className="text-[11px] text-[#8A8A82] font-body italic">
              Preview after saving to see the vellum before transmission.
            </div>
            <div className="flex gap-2">
              <button onClick={onClose}
                        className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-white/20 text-[#C9C9C2] px-4 py-2 rounded-sm">
                Cancel
              </button>
              <button data-testid="mandate-draft-save"
                        onClick={save} disabled={busy}
                        className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] px-6 py-2 rounded-sm inline-flex items-center gap-1.5">
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                {isEdit ? "Save Changes" : "Save Mandate"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Draft/Edit Sheet
   ═════════════════════════════════════════════════════════════════ */
function DraftEditorSheet({ api, kind, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [recipient, setRecipient] = useState(() => initial?.recipient || { ...EMPTY_RECIPIENT });
  const [items, setItems] = useState(() => (initial?.items?.length ? initial.items : [{ ...EMPTY_ITEM }]));
  const [paymentTerms, setPaymentTerms] = useState(initial?.payment_terms || PAYMENT_TERM_PRESETS[0]);
  const [deliveryDate, setDeliveryDate] = useState(initial?.delivery_date || "");
  const [dispatchNotes, setDispatchNotes] = useState(initial?.dispatch_notes || "");
  const [intraState, setIntraState] = useState(initial?.intra_state ?? true);
  const [gstPct, setGstPct] = useState(initial?.gst_pct ?? 18);
  const [busy, setBusy] = useState(false);

  const setItem = (idx, patch) =>
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((arr) => [...arr, { ...EMPTY_ITEM }]);
  const rmItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const grandPreview = useMemo(() => {
    const sub = items.reduce((s, it) => s + (Number(it.qty || 0) * Number(it.rate || 0)), 0);
    return Math.round(sub * (1 + Number(gstPct || 0) / 100));
  }, [items, gstPct]);

  const validate = () => {
    if (!recipient.name?.trim()) return "Recipient name required";
    if (!recipient.email?.includes("@")) return "Recipient email required";
    if (items.length === 0) return "Add at least one line item";
    for (const [i, it] of items.entries()) {
      if (!it.name?.trim()) return `Item ${i + 1}: name required`;
      const hsn = String(it.hsn || "").trim();
      if (!/^\d{4}(\d{2})?(\d{2})?$/.test(hsn))
        return `Item ${i + 1}: HSN must be 4, 6 or 8 digits`;
      if (Number(it.qty) <= 0) return `Item ${i + 1}: quantity must be > 0`;
      if (Number(it.rate) < 0) return `Item ${i + 1}: rate must be ≥ 0`;
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    const payload = {
      recipient, items,
      payment_terms: paymentTerms,
      delivery_date: deliveryDate || null,
      dispatch_notes: dispatchNotes,
      intra_state: intraState, gst_pct: Number(gstPct),
    };
    try {
      if (isEdit) {
        await api.patch(`/admin/monumental/${initial.id}`, payload);
        toast.success("Draft updated");
      } else {
        await api.post("/admin/monumental/draft", { kind, ...payload });
        toast.success("Draft created");
      }
      onSaved?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(" · ") : (d || "Save failed"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="monumental-draft-sheet"
         className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-[#0A0906] border border-[#D8B23E]/40 rounded-sm w-full max-w-4xl max-h-[90vh] overflow-y-auto"
           style={{ boxShadow: "0 40px 120px -30px rgba(216,178,62,0.4)" }}>
        <div className="sticky top-0 bg-[#0A0906] border-b border-[#D8B23E]/30 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="overline text-[#D8B23E]">Studio</div>
            <h3 className="font-serif text-2xl">{isEdit ? "Edit" : "New"} {kind === "po" ? "Purchase Order" : "Quotation"}</h3>
          </div>
          <button onClick={onClose} data-testid="monumental-draft-close"
                    className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Recipient */}
          <section>
            <div className="overline text-[#B87333]">Recipient</div>
            <h4 className="font-serif text-lg mt-1">
              {kind === "po" ? "Supplier · To" : "Buyer · Prepared For"}
            </h4>
            <p className="text-[11px] text-[#8A8A82] font-body mt-1">
              Works for both <b>listed</b> Vinci partners and <b>unlisted</b> counterparties — just enter their details below.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <TextField label="Name / Business" required
                          value={recipient.name}
                          onChange={(v) => setRecipient((r) => ({ ...r, name: v }))}
                          testid="monumental-recipient-name" />
              <TextField label="Email" required type="email"
                          value={recipient.email}
                          onChange={(v) => setRecipient((r) => ({ ...r, email: v }))}
                          testid="monumental-recipient-email" />
              <TextField label="GSTIN"
                          value={recipient.gstin}
                          onChange={(v) => setRecipient((r) => ({ ...r, gstin: v.toUpperCase() }))}
                          testid="monumental-recipient-gstin" />
              <TextField label="PAN"
                          value={recipient.pan}
                          onChange={(v) => setRecipient((r) => ({ ...r, pan: v.toUpperCase() }))} />
              <div className="md:col-span-2">
                <TextField label="Address"
                            value={recipient.address}
                            onChange={(v) => setRecipient((r) => ({ ...r, address: v }))} />
              </div>
            </div>
          </section>

          {/* Materials */}
          <section>
            <div className="flex items-center justify-between">
              <div>
                <div className="overline text-[#B87333]">Materials</div>
                <h4 className="font-serif text-lg mt-1">Line items · <span className="text-[#D8B23E]">HSN mandatory</span></h4>
              </div>
              <button onClick={addItem} data-testid="monumental-add-item"
                        className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-[#D8B23E]/40 text-[#D8B23E] hover:bg-[#D8B23E]/10 px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5">
                <Plus size={11} /> Add Row
              </button>
            </div>

            <div className="mt-3 space-y-2">
              {items.map((it, idx) => (
                <ItemRow
                  key={idx} idx={idx} item={it}
                  disableRemove={items.length <= 1}
                  onChange={(patch) => setItem(idx, patch)}
                  onRemove={() => rmItem(idx)}
                />
              ))}
            </div>
          </section>

          {/* Terms */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A] font-grotesk">Payment Terms</label>
              <select data-testid="monumental-payment-terms"
                        value={paymentTerms}
                        onChange={(e) => setPaymentTerms(e.target.value)}
                        className="w-full mt-1 bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] text-sm">
                {PAYMENT_TERM_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <TextField label="Delivery Date" type="date"
                        value={deliveryDate}
                        onChange={setDeliveryDate}
                        testid="monumental-delivery-date" />
            <TextField label="Dispatch Notes (optional)" value={dispatchNotes}
                        onChange={setDispatchNotes} />
            <div className="flex items-end gap-3">
              <label className="inline-flex items-center gap-2 text-[12px] text-[#C9C9C2] font-body">
                <input type="checkbox" checked={intraState}
                        onChange={(e) => setIntraState(e.target.checked)} />
                Intra-state (CGST + SGST)
              </label>
              <div className="flex-1">
                <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A] font-grotesk">GST %</label>
                <input type="number" value={gstPct} min={0} max={28}
                        onChange={(e) => setGstPct(e.target.value)}
                        className="w-full mt-1 bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] text-sm" />
              </div>
            </div>
          </section>

          <div className="border-t border-white/10 pt-4 flex items-center justify-between flex-wrap gap-3">
            <div className="text-[11px] text-[#8A8A82] font-body">
              Estimated Grand Total{" "}
              <b className="text-[#F6E4A6] font-mono">{inr(grandPreview)}</b>
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-white/20 text-[#C9C9C2] px-4 py-2 rounded-sm">Cancel</button>
              <button data-testid="monumental-draft-save"
                        onClick={save} disabled={busy}
                        className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] px-6 py-2 rounded-sm inline-flex items-center gap-1.5">
                {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                {isEdit ? "Save Changes" : "Save Draft"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Line-Item Row · HSN autofills from construction catalog
   ═════════════════════════════════════════════════════════════════ */
function ItemRow({ idx, item, onChange, onRemove, disableRemove }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [hsnManuallyEdited, setHsnManuallyEdited] = useState(!!item.hsn);
  const rowRef = useRef(null);

  // Debounced HSN autofill when the material name changes.
  useEffect(() => {
    const q = (item.name || "").trim();
    if (!q) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      const hits = suggestHsn(q, 5);
      setSuggestions(hits);
      // Only auto-fill HSN if the admin hasn't typed one manually yet.
      if (!hsnManuallyEdited && hits.length > 0 && !item.hsn) {
        onChange({ hsn: hits[0].hsn });
      }
    }, 180);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.name]);

  // Close suggestions when clicking outside.
  useEffect(() => {
    const onDoc = (e) => {
      if (rowRef.current && !rowRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const applySuggestion = (s) => {
    setHsnManuallyEdited(false);
    onChange({ hsn: s.hsn });
    setShowSuggest(false);
  };

  return (
    <div ref={rowRef} data-testid={`monumental-item-row-${idx}`}
         className="relative">
      <div className="grid grid-cols-[2fr_1fr_0.7fr_0.7fr_1fr_auto] gap-2 border border-white/10 rounded-sm p-2 bg-[#050505]">
        <TextField placeholder="Material name (e.g. OPC Cement 53 Grade)" value={item.name}
                    onChange={(v) => { onChange({ name: v }); setShowSuggest(true); }}
                    testid={`monumental-item-name-${idx}`} tight />
        <div className="relative">
          <TextField placeholder="HSN (auto)" value={item.hsn}
                      onChange={(v) => {
                        onChange({ hsn: v.replace(/\D/g, "").slice(0, 8) });
                        setHsnManuallyEdited(true);
                      }}
                      testid={`monumental-item-hsn-${idx}`} tight />
          {!hsnManuallyEdited && item.hsn && (
            <span title="Auto-suggested from construction HSN catalog"
                    data-testid={`monumental-item-hsn-auto-${idx}`}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-grotesk uppercase tracking-[0.18em] text-[#D8B23E] inline-flex items-center gap-1 pointer-events-none">
              <Zap size={9} /> Auto
            </span>
          )}
        </div>
        <TextField placeholder="Qty" type="number" value={item.qty}
                    onChange={(v) => onChange({ qty: v })}
                    testid={`monumental-item-qty-${idx}`} tight />
        <TextField placeholder="Unit" value={item.unit || "MT"}
                    onChange={(v) => onChange({ unit: v })} tight />
        <TextField placeholder="Rate (₹)" type="number" value={item.rate}
                    onChange={(v) => onChange({ rate: v })}
                    testid={`monumental-item-rate-${idx}`} tight />
        <button onClick={onRemove} disabled={disableRemove}
                  className="text-[#C0392B] hover:bg-[#C0392B]/10 rounded-sm px-2 disabled:opacity-30">
          <Trash2 size={14} />
        </button>
      </div>

      {showSuggest && suggestions.length > 0 && (
        <div data-testid={`monumental-hsn-suggestions-${idx}`}
             className="absolute z-20 mt-1 w-full max-w-md bg-[#0A0906] border border-[#D8B23E]/40 rounded-sm shadow-[0_20px_50px_-10px_rgba(216,178,62,0.35)] overflow-hidden">
          <div className="px-3 py-1.5 bg-[#D8B23E]/10 text-[9px] font-grotesk uppercase tracking-[0.28em] text-[#D8B23E] inline-flex items-center gap-2">
            <Zap size={9} /> Construction HSN — click to fill
          </div>
          {suggestions.map((s) => (
            <button
              key={s.hsn + s.label}
              type="button"
              onClick={() => applySuggestion(s)}
              data-testid={`monumental-hsn-suggestion-${idx}-${s.hsn}`}
              className="w-full text-left px-3 py-2 hover:bg-[#D8B23E]/10 border-t border-white/5 flex items-center justify-between gap-3"
            >
              <div>
                <div className="text-[12px] text-[#F5F5F0] font-body">{s.label}</div>
                <div className="text-[10px] text-[#8A8A82]">GST {s.gst}%</div>
              </div>
              <span className="font-mono text-[11px] text-[#D8B23E] tracking-wider">{s.hsn}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Transmit Sheet
   ═════════════════════════════════════════════════════════════════ */
function TransmitSheet({ api, row, onClose, onDone }) {
  const rec = row.recipient || {};
  const [toEmail, setToEmail] = useState(rec.email || "");
  const [toName, setToName] = useState(rec.name || "");
  const [cc, setCc] = useState("");
  const [note, setNote] = useState("");
  const [bulkCsv, setBulkCsv] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const cc_emails = cc.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
      const { data } = await api.post(`/admin/monumental/${row.id}/transmit`, {
        to_email: toEmail || undefined,
        to_name: toName || undefined,
        cc_emails, note,
        bulk_csv: bulkCsv || undefined,
      });
      toast.success(`Transmitted · ${data.delivered} recipient(s) delivered`);
      if ((data.failed || []).length > 0) {
        toast.error(`${data.failed.length} failed — check server logs`);
      }
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transmit failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="monumental-transmit-sheet"
         className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-[#0A0906] border border-[#2FB999]/40 rounded-sm w-full max-w-2xl"
           style={{ boxShadow: "0 40px 120px -30px rgba(47,185,153,0.4)" }}>
        <div className="border-b border-[#2FB999]/30 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="overline text-[#2FB999]">Transmit</div>
            <h3 className="font-serif text-2xl">Transmit {row.kind === "po" ? "PO to Supplier" : "Quote to Buyer"}</h3>
            <p className="text-[11px] text-[#8A8A82] font-body mt-1">
              {row.doc_number} · SRN {row.srn}
            </p>
          </div>
          <button onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-4">
          <TextField label="Recipient Email" required type="email"
                      value={toEmail} onChange={setToEmail}
                      testid="monumental-transmit-email" />
          <TextField label="Recipient Name" value={toName} onChange={setToName} />
          <TextField label="CC (comma-separated)" placeholder="ops@supplier.com, finance@..."
                      value={cc} onChange={setCc}
                      testid="monumental-transmit-cc" />

          <div>
            <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A] font-grotesk">Personal Note (optional)</label>
            <textarea data-testid="monumental-transmit-note"
                        value={note} onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        placeholder="Please prioritise this dispatch — order value ₹8.2L."
                        className="w-full mt-1 bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] text-sm" />
          </div>

          <div>
            <button onClick={() => setShowBulk((v) => !v)}
                      data-testid="monumental-transmit-bulk-toggle"
                      className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#D8B23E] hover:underline inline-flex items-center gap-1">
              <Users size={11} /> {showBulk ? "Hide bulk paste" : "Bulk send · paste CSV"}
            </button>
            {showBulk && (
              <div className="mt-2">
                <textarea data-testid="monumental-transmit-bulk-csv"
                            value={bulkCsv} onChange={(e) => setBulkCsv(e.target.value)}
                            rows={4}
                            placeholder={"one row per recipient:\nemail,name\nops@steel.co,Steel Traders Ltd\nsales@cement.in,Cement HQ"}
                            className="w-full bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0] text-xs font-mono" />
                <div className="text-[10px] text-[#8A8A82] font-body italic mt-1">
                  Each row is emailed the same PDF. Duplicates auto-deduped case-insensitively.
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-white/10 px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="text-[10px] font-grotesk uppercase tracking-[0.22em] border border-white/20 text-[#C9C9C2] px-4 py-2 rounded-sm">Cancel</button>
          <button data-testid="monumental-transmit-send"
                    onClick={send} disabled={busy || !toEmail}
                    className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#2FB999] to-[#5FD3B7] px-6 py-2 rounded-sm inline-flex items-center gap-1.5">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            Transmit to {row.kind === "po" ? "Supplier" : "Buyer"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   Tiny reusable input
   ═════════════════════════════════════════════════════════════════ */
function TextField({ label, value, onChange, testid, type = "text",
                     placeholder = "", required = false, tight = false }) {
  return (
    <div className={tight ? "" : ""}>
      {label && !tight && (
        <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A] font-grotesk">
          {label}{required && <span className="text-[#D8B23E]"> *</span>}
        </label>
      )}
      <input
        data-testid={testid}
        type={type} value={value ?? ""}
        placeholder={placeholder || label}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full ${tight ? "" : "mt-1"} bg-black/50 border border-white/20 focus:border-[#D8B23E] rounded-sm px-3 py-2 text-[#F5F5F0] text-sm outline-none transition-colors`}
      />
    </div>
  );
}
